import { useEffect, useRef, useState, type FormEvent } from "react";
import type { Invite } from "../api";
import { t, when, word, phrases } from "../i18n";
import { CopyButton } from "./Copy";
import { useModalKeys } from "./Modal";
import { Popover } from "./Popover";

// An invite link made from a page's panel (byj-1wv.51.3, byj-1wv.51.4): a
// share's, for the role picked, and a recording's, for a reviewer. Invite
// opens it as a popover (components/Popover) the width of the panel it is
// over, so the side panel keeps its place and it fits however narrow that
// is. Open, the focus is on the role picked, or on how many when there is no
// role to pick; Esc gives it back to Invite, and a press outside, or ✕,
// closes it. What was picked stays picked when it opens again, and the links
// made in it are the page's (`made`): the relay keeps only a link's hash, so
// it is shown nowhere else. A page with no panel for it to hang from — an
// app template's — opens the same form in a dialog (InviteLinkDialog).

/** An invite link made on this page, with what it was made for. */
export type MadeInvite = Invite & { role: string; uses: number };

/** A role an invite may be for, named, saying what it may do. */
export type InviteRoleOption = { id: string; name: string; what: string };

/** What an invite is made for: who, how many (0 for anyone with the link), for how long. */
export type InviteChoice = { role: string; uses: number; ttl: number };

/** How many may use a link: a count, or 0 for anyone who has it (the relay counts none). */
export const usesOptions = [1, 5, 10, 25, 0];

/**
 * A count alone, never anyone with the link: a recording's reviewer link, as
 * only the reviewers its owner invites may open a recording (RFC-043
 * "Recordings"), and as the relay's own review requests are for one.
 */
export const countedUses = usesOptions.filter((n) => n > 0);

export function InviteLinkPopover({ id, roles, uses, initial, create, made, onMade, note }: {
  /** unique on the page: names the role radio group */
  id: string;
  /** the roles to pick from; none, or one, and there is nothing to pick */
  roles?: InviteRoleOption[];
  /** how many may use a link, as offered (usesOptions when not said) */
  uses?: number[];
  initial: InviteChoice;
  create: (c: InviteChoice) => Promise<Invite>;
  made: MadeInvite[];
  onMade: (inv: MadeInvite) => void;
  /** a line under the head: why a role is not offered */
  note?: string;
}) {
  const [choice, setChoice] = useState<InviteChoice>(initial);
  const picking = (roles?.length ?? 0) > 1;
  return (
    <Popover
      label={t("session:inviteCreate")}
      button={t("session:invite")}
      buttonClassName="small"
      className="invite-anchor"
      popClassName="invite-pop"
      focus={picking ? "input[type=radio]:checked" : "select"}
    >
      {(close) => (
        <>
          <div className="invite-pop-head">
            <h4>{t("session:inviteCreate")}</h4>
            <button type="button" className="small" aria-label={t("common:close")} onClick={close}>
              ✕
            </button>
          </div>
          {note && <p className="muted small" data-testid="invite-note">{note}</p>}
          <InviteLinkForm id={id} roles={roles} uses={uses} choice={choice} onChoice={setChoice} create={create} made={made} onMade={onMade} />
        </>
      )}
    </Popover>
  );
}

/**
 * The invite itself, in sections a rule apart: who it is for, a radio button
 * per role offered, each saying what it may do (left out when there is no
 * choice); for how many and how long, a line; Create link; then the links
 * made on this page, newest first, each to copy.
 */
export function InviteLinkForm({ id, roles, uses: offered = usesOptions, choice, onChoice, create, made, onMade }: {
  id: string;
  roles?: InviteRoleOption[];
  uses?: number[];
  choice: InviteChoice;
  onChoice: (c: InviteChoice) => void;
  create: (c: InviteChoice) => Promise<Invite>;
  made: MadeInvite[];
  onMade: (inv: MadeInvite) => void;
}) {
  const { role, uses, ttl } = choice;
  const [error, setError] = useState<string | null>(null);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    try {
      onMade({ ...(await create(choice)), role, uses });
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  };

  return (
    <form className="invite" onSubmit={submit}>
      {roles && roles.length > 1 && (
        <fieldset className="invite-section invite-roles">
          <legend>{t("session:inviteRole")}</legend>
          {roles.map((r) => (
            <label key={r.id} className={`invite-role${role === r.id ? " picked" : ""}`}>
              <input type="radio" name={`invite-role-${id}`} value={r.id} checked={role === r.id} onChange={() => onChoice({ ...choice, role: r.id })} />
              <span>
                <strong>{r.name}</strong>
                <span className="muted small">{r.what}</span>
              </span>
            </label>
          ))}
        </fieldset>
      )}
      <div className="invite-section invite-line">
        <label>
          {t("session:inviteFor")}
          <select value={uses} onChange={(e) => onChoice({ ...choice, uses: Number(e.target.value) })}>
            {offered.map((n) => (
              <option key={n} value={n}>
                {phrases.inviteUsesOption(n)}
              </option>
            ))}
          </select>
        </label>
        <label>
          {t("session:inviteExpires")}
          <select value={ttl} onChange={(e) => onChoice({ ...choice, ttl: Number(e.target.value) })}>
            {phrases.ttlOptions().map(([s, label]) => (
              <option key={s} value={s}>
                {label}
              </option>
            ))}
          </select>
        </label>
      </div>
      <div className="invite-section">
        <button type="submit" className="primary invite-create">
          {t("session:createInvite")}
        </button>
      </div>
      {error && <p className="error">{t("common:error", { m: error })}</p>}
      {made.length > 0 && (
        <section className="invite-made" aria-label={t("session:invitesMade")}>
          <h4>{t("session:invitesMade")}</h4>
          <p className="muted small">{t("session:invitesMadeWhy")}</p>
          <ul>
            {made.map((inv) => (
              <li key={inv.url}>
                <div className="row invite-url">
                  <input readOnly value={inv.url} onFocus={(e) => e.target.select()} aria-label={t("session:inviteCreate")} />
                  <CopyButton text={inv.url} />
                </div>
                <p className="muted small">{phrases.inviteMade(word("common:role", inv.role) ?? inv.role, inv.uses, when(inv.expires_at))}</p>
              </li>
            ))}
          </ul>
        </section>
      )}
    </form>
  );
}

/**
 * The invite form in a dialog over the page, as the page's other short
 * questions are (Confirm, a new class) — where there is no panel for a
 * popover to hang from: an app template's page (byj-1wv.51.14). Not a
 * drawer: on a page the screen's width one opened at its far edge, away from
 * Invite and from what it is about. `note` says what accepting the link
 * gives. Open, the focus is on how many; Esc, its ✕ or a press outside
 * closes it, and the opener gives the focus back to Invite (`onClose`).
 */
export function InviteLinkDialog({ id, note, choice, onChoice, create, made, onMade, onClose }: {
  /** unique on the page: the dialog's id is `${id}-invite`, for Invite's aria-controls */
  id: string;
  note?: string;
  choice: InviteChoice;
  onChoice: (c: InviteChoice) => void;
  create: (c: InviteChoice) => Promise<Invite>;
  made: MadeInvite[];
  onMade: (inv: MadeInvite) => void;
  onClose: () => void;
}) {
  const box = useRef<HTMLDivElement>(null);
  const keys = useModalKeys(onClose);
  useEffect(() => {
    box.current?.querySelector<HTMLElement>("select")?.focus();
  }, []);
  return (
    <div className="modal-backdrop" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div ref={box} id={`${id}-invite`} className="modal invite-modal stack" role="dialog" aria-modal="true" aria-labelledby={`${id}-invite-title`} onKeyDown={keys}>
        <div className="row">
          <h3 id={`${id}-invite-title`} className="grow flush">
            {t("session:inviteCreate")}
          </h3>
          <button type="button" className="small" aria-label={t("common:close")} onClick={onClose}>
            ✕
          </button>
        </div>
        {note && <p className="muted small flush">{note}</p>}
        <InviteLinkForm id={id} choice={choice} onChoice={onChoice} create={create} made={made} onMade={onMade} />
      </div>
    </div>
  );
}
